import React, { useEffect, useMemo, useState } from 'react';
import { Button, Divider, Select, Space, Spin, Tooltip, theme } from 'antd';
import type { SelectProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { Item } from '@/services/inventory.service';
import { useItems } from '@/hooks/useInventory';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useAuthStore } from '@/store/authStore';
import { QuickCreateItemModal } from '@/components/inventory/QuickCreateItemModal';
import { CriticalItemTag, isItemCritical } from '@/components/inventory/criticalItemUi';

export type ItemLike = Pick<
  Item,
  | 'id'
  | 'item_code'
  | 'item_name'
  | 'item_description'
  | 'item_type'
  | 'base_uom'
  | 'specifications'
  | 'profile_grade'
  | 'profile_heat_no'
  | 'profile_dimension'
  | 'profile_length'
  | 'is_critical'
  | 'measurement_type'
  | 'default_width_mm'
  | 'default_height_mm'
> & {
  category?: { category_name?: string; category_code?: string } | null;
  category_name?: string | null;
  current_stock?: number | null;
  last_purchase_price?: number | null;
  standard_cost?: number | null;
  average_cost?: number | null;
  purchase_uom?: string | null;
  sales_uom?: string | null;
};

/** Base UOM from an ItemSelect change option, when the picker included the item. */
export function inventoryUomFromSelectOption(option: unknown): string | null {
  if (!option || Array.isArray(option)) return null;
  const item = (option as { item?: { base_uom?: string | null } }).item;
  const uom = String(item?.base_uom || '').trim();
  return uom || null;
}

export function formatItemSelectLabel(item: ItemLike): string {
  const code = String(item.item_code ?? '').trim();
  const name = String(item.item_name ?? '').trim();
  if (code && name) return `${code} — ${name}`;
  return code || name || item.id;
}

/** Compact Grade · Heat No · Dim meta (excludes Length — PR line has its own Length field). */
export function formatItemProfileMeta(
  item: Pick<ItemLike, 'profile_grade' | 'profile_heat_no' | 'profile_dimension' | 'profile_length'>,
  opts?: { includeLength?: boolean }
): string {
  const grade = String(item.profile_grade ?? '').trim();
  const heat = String(item.profile_heat_no ?? '').trim();
  const dimension = String(item.profile_dimension ?? '').trim();
  const length = String(item.profile_length ?? '').trim();
  const parts = [
    grade ? `Grade ${grade}` : '',
    heat ? `Heat No ${heat}` : '',
    dimension ? `Dim ${dimension}` : '',
    opts?.includeLength && length ? `Length ${length}` : '',
  ].filter(Boolean);
  return parts.join(' · ');
}

export type ItemProfileFields = Pick<
  ItemLike,
  'profile_grade' | 'profile_heat_no' | 'profile_dimension' | 'profile_length'
>;

/**
 * Join base description with profile meta (Grade · Heat · Dim [· Length]).
 * Idempotent when the profile string is already present in the base text.
 * When `profile.item_name` / `item_description` is set and base is empty/short name, upgrades to catalog text.
 * Keep in sync with backend `item-description-profile.util.ts`.
 */
export function formatItemDescriptionWithProfile(
  baseDescription: string | null | undefined,
  profile:
    | (ItemProfileFields & {
        item_name?: string | null;
        item_description?: string | null;
        item_code?: string | null;
      })
    | null
    | undefined,
  opts?: { includeLength?: boolean }
): string {
  const meta = formatItemProfileMeta(profile ?? {}, opts);
  const fullDesc = String(profile?.item_description ?? '').trim();
  const itemName = String(profile?.item_name ?? '').trim();
  let base = String(baseDescription ?? '').trim();

  if (fullDesc) {
    const firstLine = fullDesc.split(/\r?\n/)[0]?.trim() ?? '';
    if (!base) {
      base = fullDesc;
    } else if (itemName && base === itemName && (firstLine === itemName || fullDesc.startsWith(itemName))) {
      base = fullDesc;
    } else if (firstLine && base === firstLine) {
      base = fullDesc;
    } else if (fullDesc.startsWith(base) && fullDesc.length > base.length) {
      const next = fullDesc[base.length];
      if (!next || /\s/.test(next)) base = fullDesc;
    }
  } else if (itemName) {
    const profileOnly =
      !base ||
      (Boolean(meta) &&
        (base === meta || /^\s*(Grade |Heat No |Dim )/.test(base)) &&
        !base.includes(itemName));
    if (profileOnly) {
      base = itemName;
    }
  }

  if (!meta) return base;
  if (!base) return meta;
  if (base.includes(meta)) return base;
  if (/\bGrade\s|\bHeat No\s|\bDim\s/.test(base)) return base;
  return `${base} — ${meta}`;
}

/** Catalog pick → line description (full item_description preferred, else name + profile). */
export function formatCatalogItemDescription(
  item: Pick<ItemLike, 'item_name' | 'item_description' | 'item_code'> & ItemProfileFields,
  opts?: { includeLength?: boolean }
): string {
  const base = String(item.item_description || item.item_name || item.item_code || '').trim();
  return formatItemDescriptionWithProfile(base, item, opts);
}

export function buildItemSearchText(item: ItemLike): string {
  const categoryName =
    item.category?.category_name || item.category?.category_code || item.category_name || '';
  const grade = String(item.profile_grade ?? '').trim();
  const heat = String(item.profile_heat_no ?? '').trim();
  const dimension = String(item.profile_dimension ?? '').trim();
  const length = String(item.profile_length ?? '').trim();
  return [
    item.item_code,
    item.item_name,
    item.item_description,
    item.specifications,
    item.base_uom,
    item.item_type,
    categoryName,
    isItemCritical(item) ? 'critical' : '',
    grade,
    grade ? `Grade ${grade}` : '',
    heat,
    heat ? `Heat ${heat}` : '',
    heat ? `Heat No ${heat}` : '',
    heat ? `Heat No. ${heat}` : '',
    dimension,
    dimension ? `Dimension ${dimension}` : '',
    length,
    length ? `Length ${length}` : '',
    length ? `Len ${length}` : '',
  ]
    .filter((s) => s != null && String(s).trim() !== '')
    .join(' ')
    .toLowerCase();
}

function categoryLabel(item: ItemLike): string {
  return String(item.category?.category_name || item.category_name || '').trim();
}

export function ItemOptionContent({
  item,
  stockQty,
  showStock,
}: {
  item: ItemLike;
  stockQty?: number | null;
  showStock?: boolean;
}) {
  const { token } = theme.useToken();
  const grade = String(item.profile_grade ?? '').trim();
  const heat = String(item.profile_heat_no ?? '').trim();
  const dimension = String(item.profile_dimension ?? '').trim();
  const length = String(item.profile_length ?? '').trim();
  const metaParts = [
    item.base_uom,
    categoryLabel(item),
    item.item_type,
    grade ? `Grade ${grade}` : '',
    heat ? `Heat No ${heat}` : '',
    dimension ? `Dim ${dimension}` : '',
    length ? `Length ${length}` : '',
  ].filter((s): s is string => Boolean(s && String(s).trim()));
  if (showStock && stockQty != null && Number.isFinite(Number(stockQty))) {
    metaParts.push(`Free ${Number(stockQty).toLocaleString()}`);
  }
  const descParts = [item.item_description, item.specifications].filter(
    (s): s is string => Boolean(s && String(s).trim())
  );
  const desc = descParts.join(' · ');

  const critical = isItemCritical(item);

  return (
    <div
      className={critical ? 'scm-critical-item-option' : undefined}
      style={{
        padding: '2px 0',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        maxWidth: 520,
      }}
    >
      <div style={{ fontWeight: 600, lineHeight: 1.4 }}>
        <Space size={6} wrap>
          <span>
            <span style={{ marginRight: 6 }}>{item.item_code}</span>
            <span>— {item.item_name}</span>
          </span>
          <CriticalItemTag critical={critical} />
        </Space>
      </div>
      {metaParts.length > 0 ? (
        <div
          style={{
            fontSize: token.fontSizeSM,
            color: token.colorTextSecondary,
            marginTop: 4,
            lineHeight: 1.4,
          }}
        >
          {metaParts.join(' · ')}
        </div>
      ) : null}
      {desc ? (
        <div
          style={{
            fontSize: token.fontSizeSM,
            color: token.colorTextSecondary,
            marginTop: 4,
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {desc}
        </div>
      ) : null}
    </div>
  );
}

export type ItemSelectProps = Omit<
  SelectProps,
  'options' | 'optionFilterProp' | 'optionLabelProp' | 'filterOption' | 'showSearch' | 'popupRender'
> & {
  items?: ItemLike[];
  /** When items omitted, fetch via useItems (server search + limit for pickers). */
  autoLoad?: boolean;
  /** Max rows when auto-loading (default 80). */
  autoLoadLimit?: number;
  showStock?: boolean;
  stockByItemId?: Record<string, number> | Map<string, number>;
  /** Show Create new item (lean master + optional opening stock). Default false. */
  allowQuickCreate?: boolean;
  /** After quick-create; parent should append to list / refresh. */
  onItemCreated?: (item: Item) => void;
  /** Prefill selected options so edit forms keep values outside the search page. */
  seedItems?: ItemLike[];
  /** Keep only these item types (for example Stock and Consumable). */
  allowedItemTypes?: string[];
};

function resolveStock(
  itemId: string,
  stockByItemId?: Record<string, number> | Map<string, number>
): number | undefined {
  if (!stockByItemId) return undefined;
  if (stockByItemId instanceof Map) {
    const v = stockByItemId.get(itemId);
    return v == null ? undefined : Number(v);
  }
  const v = stockByItemId[itemId];
  return v == null ? undefined : Number(v);
}

/**
 * Inventory item picker with rich option rows (code, name, UOM, category, profile, description).
 * Closed field shows `code — name`.
 * Search matches code/name/description/specs/category/grade/heat no/dimension/length.
 * Optional quick-create for BOQ / estimation when item is missing.
 */
export const ItemSelect: React.FC<ItemSelectProps> = ({
  items: itemsProp,
  autoLoad = true,
  autoLoadLimit = 80,
  showStock = false,
  stockByItemId,
  loading: loadingProp,
  placeholder = 'Search and select an item',
  allowClear = true,
  allowQuickCreate = false,
  onItemCreated,
  seedItems,
  allowedItemTypes,
  disabled,
  onChange,
  value,
  ...rest
}) => {
  const { token } = theme.useToken();
  const user = useAuthStore((s) => s.user);
  const perms = (user?.permissions ?? []) as string[];
  const canCreate =
    !allowQuickCreate ||
    perms.includes('inventory.item.create') ||
    perms.includes('inventory.view') ||
    perms.length === 0;

  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedCache, setSelectedCache] = useState<ItemLike[]>([]);
  const debouncedSearch = useDebouncedValue(search.trim(), 300);

  const shouldLoad = autoLoad && itemsProp == null;
  const { data: loadedItems = [], isLoading, isFetching, isError } = useItems(
    {
      is_active: true,
      search: debouncedSearch || undefined,
      limit: autoLoadLimit,
    },
    { enabled: shouldLoad }
  );

  useEffect(() => {
    if (!seedItems?.length) return;
    setSelectedCache((prev) => {
      const byId = new Map(prev.map((p) => [p.id, p]));
      let changed = false;
      for (const seed of seedItems) {
        if (!seed?.id) continue;
        if (!byId.has(seed.id)) {
          byId.set(seed.id, seed);
          changed = true;
        }
      }
      return changed ? Array.from(byId.values()) : prev;
    });
  }, [seedItems]);

  useEffect(() => {
    if (value == null || value === '') return;
    const id = String(value);
    const fromLoaded = (itemsProp ?? loadedItems).find((i) => i.id === id);
    if (!fromLoaded) return;
    setSelectedCache((prev) => (prev.some((p) => p.id === id) ? prev : [...prev, fromLoaded]));
  }, [value, itemsProp, loadedItems]);

  const items = useMemo(() => {
    const base = itemsProp ?? (shouldLoad ? loadedItems : []);
    const allowed = allowedItemTypes?.map((t) => t.toLowerCase());
    const filtered = allowed?.length
      ? base.filter((it) => allowed.includes(String(it.item_type || '').toLowerCase()))
      : base;
    const byId = new Map<string, ItemLike>();
    for (const it of filtered) byId.set(it.id, it);
    for (const it of selectedCache) {
      if (!byId.has(it.id)) byId.set(it.id, it);
    }
    return Array.from(byId.values());
  }, [itemsProp, shouldLoad, loadedItems, selectedCache, allowedItemTypes]);

  const loading = loadingProp ?? (shouldLoad ? isLoading || isFetching : false);

  const options = useMemo(
    () =>
      items.map((item) => {
        const stockQty = showStock ? resolveStock(item.id, stockByItemId) : undefined;
        return {
          value: item.id,
          label: formatItemSelectLabel(item),
          searchText: buildItemSearchText(item),
          item,
          stockQty,
        };
      }),
    [items, showStock, stockByItemId]
  );

  const preventClose: React.MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const openCreate = () => {
    if (!allowQuickCreate || disabled || !canCreate) return;
    setCreateOpen(true);
  };

  const handleCreated = (item: Item) => {
    setCreateOpen(false);
    setSearch('');
    setSelectedCache((prev) => [...prev.filter((p) => p.id !== item.id), item]);
    onItemCreated?.(item);
    if (!onItemCreated) {
      onChange?.(item.id, { value: item.id, label: formatItemSelectLabel(item), item } as any);
    }
  };

  const handleChange: SelectProps['onChange'] = (val, option) => {
    if (val != null && option && !Array.isArray(option)) {
      const item = (option as { item?: ItemLike }).item;
      if (item) {
        setSelectedCache((prev) => [...prev.filter((p) => p.id !== item.id), item]);
      }
    }
    onChange?.(val, option);
  };

  const createBtn = (
    <Button
      type="text"
      size="small"
      icon={<PlusOutlined />}
      disabled={disabled || !canCreate}
      onMouseDown={preventClose}
      onClick={openCreate}
      style={{ width: '100%', textAlign: 'left', color: token.colorPrimary }}
    >
      Create new item
    </Button>
  );

  return (
    <>
      <Select
        {...rest}
        value={value}
        showSearch
        allowClear={allowClear}
        placeholder={placeholder}
        loading={loading}
        disabled={disabled}
        onChange={handleChange}
        onSearch={setSearch}
        filterOption={
          shouldLoad
            ? false
            : (input, option) => {
                const q = String(input ?? '')
                  .trim()
                  .toLowerCase();
                if (!q) return true;
                const searchText = String((option as { searchText?: string })?.searchText ?? '').toLowerCase();
                const label = String((option as { label?: string })?.label ?? '').toLowerCase();
                const haystack = `${searchText} ${label}`;
                const tokens = q.split(/\s+/).filter(Boolean);
                return tokens.every((t) => haystack.includes(t));
              }
        }
        optionLabelProp="label"
        optionFilterProp="searchText"
        popupMatchSelectWidth={false}
        dropdownStyle={{ minWidth: 420 }}
        virtual={false}
        options={options.map((o) => ({
          value: o.value,
          label: o.label,
          searchText: o.searchText,
          item: o.item,
          stockQty: o.stockQty,
        }))}
        optionRender={(oriOption) => {
          const data = oriOption.data as {
            item?: ItemLike;
            stockQty?: number;
          };
          if (!data?.item) return oriOption.label;
          return (
            <ItemOptionContent item={data.item} stockQty={data.stockQty} showStock={showStock} />
          );
        }}
        notFoundContent={
          loading ? (
            <div style={{ padding: 8, textAlign: 'center' }}>
              <Spin size="small" />
            </div>
          ) : isError && shouldLoad ? (
            <div style={{ padding: '4px 8px', color: token.colorTextSecondary }}>Failed to load items</div>
          ) : allowQuickCreate ? (
            <div style={{ padding: '4px 8px', color: token.colorTextSecondary }}>No matching item</div>
          ) : (
            <div style={{ padding: '4px 8px', color: token.colorTextSecondary }}>
              {shouldLoad ? 'Type to search items' : 'No items'}
            </div>
          )
        }
        popupRender={
          allowQuickCreate
            ? (menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  {!canCreate ? (
                    <Tooltip title="You need inventory.item.create permission">
                      <span>{createBtn}</span>
                    </Tooltip>
                  ) : (
                    createBtn
                  )}
                </>
              )
            : undefined
        }
      />
      {allowQuickCreate ? (
        <QuickCreateItemModal
          open={createOpen}
          presetName={search}
          onCancel={() => setCreateOpen(false)}
          onCreated={handleCreated}
        />
      ) : null}
    </>
  );
};

export default ItemSelect;
